import type { LightOrDark, OverlayTriggerState } from '@meterup/atto';
import type { PropsOf } from '@meterup/atto/src/utilities/types/polymorphicAsProp';
import type { MouseEventHandler } from 'react';
import {
  Alert,
  BodySansSizes,
  colors,
  Dialog,
  DialogContent,
  DialogHeader,
  fade,
  fontWeights,
  isLightOrDark,
  Logo,
  palette,
  selectors,
  shadows,
  Small,
  SmallSansSizes,
  styled,
  Text,
  useDialogState,
} from '@meterup/atto';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { CaptivePortal as CaptivePortalGQLType } from '../gql/graphql';
import { CaptivePortalDefaults } from '../helpers/utils/captivePortal';

const PortalLogo = styled('img', {
  width: '$200',
  height: '$120',
  objectFit: 'contain',
});

const PortalBrandedLabel = styled(Text, {
  whiteSpace: 'nowrap',

  ...SmallSansSizes,

  variants: {
    recolor: {
      light: {
        color: `${colors.bodyNeutralLight} !important`,
      },
      dark: {
        color: `${colors.white} !important`,
      },
      unrecognized: {
        color: `${colors.bodyNeutralLight} !important`,
      },
    },
  },
});

const PortalBrandedLogo = styled(Logo, {
  padding: '$3 $5 $5',
  backgroundColor: colors.iconBrandLight,
  borderRadius: '$4',

  variants: {
    recolor: {
      light: {
        color: `${colors.white} !important`,
      },
      dark: {
        color: `${colors.white} !important`,
      },
      unrecognized: {
        color: `${colors.white} !important`,
      },
    },
  },
});

const PortalBranded = styled('a', {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: '$6',
  padding: '$4 $6',
  borderRadius: '$8',

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    recolor: {
      light: {
        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
        },
      },
      dark: {
        [selectors.focus]: {
          boxShadow: shadows.focusRingDark,
        },
      },
      unrecognized: {
        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
        },
      },
    },
  },
});

const PortalHeader = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$12',
  width: '100%',

  '@notMobile': {
    padding: '$40 $40 0',
  },
});

const PortalFieldLabel = styled(Text, {
  fontWeight: fontWeights.bold,

  ...BodySansSizes,

  variants: {
    recolor: {
      light: {
        color: palette.black,
      },
      dark: {
        color: palette.white,
      },
      unrecognized: {
        color: palette.black,
      },
    },
  },
});

const PortalFieldInput = styled('input', {
  backgroundColor: 'transparent',
  width: '100%',
  padding: '$4 $8',
  borderRadius: '$8',

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    recolor: {
      light: {
        color: palette.black,

        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
        },
      },
      dark: {
        color: palette.white,

        [selectors.focus]: {
          boxShadow: shadows.focusRingDark,
        },
      },
      unrecognized: {
        color: palette.black,

        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
        },
      },
    },
  },
});

const PortalField = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
  width: '100%',
});

const PortalFields = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$12',
  width: '100%',

  '@notMobile': {
    padding: '0 $40',
  },
});

const PortalButtonLabel = styled(Text, {
  fontWeight: fontWeights.bold,
});

const PortalButton = styled('a', {
  display: 'block',
  width: '100%',
  padding: '$10 $12',
  borderRadius: '$12',
  textAlign: 'center',
  textDecoration: 'none',
  transition: 'background-color 0.2s ease-in-out',

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    recolor: {
      light: {
        strokeAll: fade(palette.black, 0.25),

        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
        },
      },
      dark: {
        strokeAll: fade(palette.white, 0.25),

        [selectors.focus]: {
          boxShadow: shadows.focusRingDark,
        },
      },
      unrecognized: {
        strokeAll: fade(palette.black, 0.25),

        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
        },
      },
    },
  },
});

const PortalTermsLink = styled('a', {
  borderRadius: '$4',
  cursor: 'pointer',
  textDecoration: 'underline',

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    recolor: {
      light: {
        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
        },
      },
      dark: {
        [selectors.focus]: {
          boxShadow: shadows.focusRingDark,
        },
      },
      unrecognized: {
        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,
        },
      },
    },
  },
});

const PortalTerms = styled(Text, {
  textAlign: 'center',

  ...SmallSansSizes,
});

const PortalFooter = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$12',
  width: '100%',

  '@notMobile': {
    padding: '0 $40 $40',
  },
});

const PortalWaitingHeading = styled('span', {
  fontWeight: fontWeights.bold,
  ...BodySansSizes,
});

const PortalWaitingSubheading = styled('span', {
  ...SmallSansSizes,
});

const PortalWaitingContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$4',
});

const PortalWaiting = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$16',
  padding: '$32 0',
});

const PortalContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '$20',
  width: '100%',
  maxWidth: '$400',
  height: '100%',

  '@notDesktop': {
    padding: '$20',
  },

  '@desktop': {
    padding: '5% 0',
  },
});

export const PortalWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',

  variants: {
    relation: {
      nested: {
        widthAll: '100%',
        heightAll: '100%',
      },
      standalone: {
        widthAll: '100vw',
        heightAll: '100dvh',
      },
    },
  },
});

type CaptivePortalData = Pick<
  CaptivePortalGQLType,
  | 'callToAction'
  | 'displaySettings'
  | 'redirectURL'
  | 'termsAndConditions'
  | 'logoImageURL'
  | 'logoImageBase64'
  | 'requiresName'
  | 'requiresEmail'
  | 'requiresApproval'
  | 'isCompanyScoped'
>;

type CaptivePortalProps = {
  captivePortal: CaptivePortalData;
  confirmLink?: string;
  relation: 'nested' | 'standalone';
};

function PortalTermsDialog({
  state,
  termsAndConditions,
}: {
  state: OverlayTriggerState;
  termsAndConditions: string;
}) {
  return (
    <Dialog state={state}>
      <DialogHeader heading="Terms & conditions" />
      <DialogContent>
        <Small whitespace="pre-wrap">{termsAndConditions}</Small>
      </DialogContent>
    </Dialog>
  );
}

async function doGET(url: string): Promise<unknown> {
  const response = await fetch(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return true;
}

export function CaptivePortal({ captivePortal, confirmLink, relation }: CaptivePortalProps) {
  const { state: termsDialogState } = useDialogState();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');

  const dynamicConfirmLink = useMemo(() => {
    if (confirmLink) {
      const url = new URL(confirmLink);
      const params = new URLSearchParams(url.search);
      if (name) params.set('name', encodeURIComponent(name.trim()));
      if (email) params.set('email', encodeURIComponent(email.trim()));
      url.search = params.toString();
      return url.toString();
    }
    return undefined;
  }, [name, email, confirmLink]);

  const captivePortalRequest = useMutation(doGET);

  const lightOrDarkBackground = useMemo((): LightOrDark => {
    if (!captivePortal.displaySettings?.backgroundColor) {
      return 'light';
    }
    const result = isLightOrDark(captivePortal.displaySettings.backgroundColor);
    return result === 'unrecognized' ? 'light' : result; // Default to 'light' if 'unrecognized'
  }, [captivePortal]);

  const backgroundColor = useMemo(() => {
    if (!captivePortal.displaySettings?.backgroundColor) {
      return CaptivePortalDefaults.displaySettings.backgroundColor;
    }
    return `${captivePortal.displaySettings.backgroundColor} !important`;
  }, [captivePortal.displaySettings?.backgroundColor]);

  const fontColor = useMemo(() => {
    if (!captivePortal.displaySettings?.fontColor) {
      return CaptivePortalDefaults.displaySettings.fontColor;
    }
    return `${captivePortal.displaySettings.fontColor} !important`;
  }, [captivePortal.displaySettings?.fontColor]);

  const callToActionColor = useMemo(() => {
    if (!captivePortal.displaySettings?.callToActionColor) {
      return CaptivePortalDefaults.displaySettings.callToActionColor;
    }
    return `${captivePortal.displaySettings.callToActionColor} !important`;
  }, [captivePortal.displaySettings?.callToActionColor]);

  const imageSource = useMemo(() => {
    if (captivePortal.logoImageBase64) {
      const { imageType, data } = captivePortal.logoImageBase64;
      let imageContentType = imageType.toLowerCase();
      if (imageType === 'SVG') {
        imageContentType = 'svg+xml';
      }
      return `data:image/${imageContentType};base64,${data}`;
    }
    return captivePortal.logoImageURL ?? CaptivePortalDefaults.logoImageURL;
  }, [captivePortal]);

  const fieldCSS = useMemo(
    () => ({
      backdropFilter:
        lightOrDarkBackground === 'light'
          ? `contrast(90%) invert(0.05) opacity(0.6)`
          : `contrast(90%) invert(0.1) opacity(0.85)`,
      color: fontColor,
    }),
    [fontColor, lightOrDarkBackground],
  );

  const buttonLabelProps: Pick<PropsOf<typeof PortalButton>, 'css' | 'recolor'> = {
    css: captivePortal.displaySettings?.callToActionColor
      ? { color: callToActionColor }
      : { color: CaptivePortalDefaults.displaySettings.callToActionColor },
    recolor: captivePortal.displaySettings?.callToActionColor ? undefined : lightOrDarkBackground,
  };

  const poweredByProps: Pick<PropsOf<typeof PortalBrandedLabel>, 'css' | 'recolor'> = {
    css: captivePortal.displaySettings?.fontColor
      ? { color: fontColor }
      : { color: CaptivePortalDefaults.displaySettings.fontColor },
    recolor: captivePortal.displaySettings?.fontColor ? undefined : lightOrDarkBackground,
  };

  const [searchParams] = useSearchParams();
  const onClickConfirm = useCallback<MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      const isPreview = searchParams.get('preview') === '1';
      let hasError = false;

      if (captivePortal.requiresName && !name.trim()) {
        setNameError('Name is required.');
        hasError = true;
      } else {
        setNameError('');
      }

      if (captivePortal.requiresEmail && !email.trim()) {
        setEmailError('Email is required.');
        hasError = true;
      } else {
        setEmailError('');
      }

      if (hasError || !dynamicConfirmLink || isPreview) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Approval flow has unique handling.
      if (captivePortal.requiresApproval) {
        e.preventDefault();
        e.stopPropagation();
        captivePortalRequest.mutate(dynamicConfirmLink);
      }
    },
    [name, email, dynamicConfirmLink, searchParams, captivePortal, captivePortalRequest],
  );

  return (
    <PortalWrapper relation={relation} css={{ backgroundColor }}>
      <PortalContent>
        <PortalHeader>
          <PortalLogo alt="company logo" src={imageSource} crossOrigin="anonymous" />
        </PortalHeader>
        {(!captivePortal.requiresApproval || captivePortalRequest.isIdle) && (
          <PortalFields>
            {captivePortal.requiresName && (
              <PortalField>
                <PortalFieldLabel css={{ color: fontColor }}>Name</PortalFieldLabel>
                {nameError && <Alert copy={nameError} variant="negative" type="inline" />}
                <PortalFieldInput
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  recolor={lightOrDarkBackground}
                  css={fieldCSS}
                />
              </PortalField>
            )}
            {captivePortal.requiresEmail && (
              <PortalField>
                <PortalFieldLabel css={{ color: fontColor }}>Email address</PortalFieldLabel>
                {emailError && <Alert copy={emailError} variant="negative" type="inline" />}
                <PortalFieldInput
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  recolor={lightOrDarkBackground}
                  css={fieldCSS}
                />
              </PortalField>
            )}
          </PortalFields>
        )}
        {captivePortal.requiresApproval && (
          <>
            {captivePortalRequest.isLoading && (
              <PortalWaiting>
                <PortalWaitingContent>
                  <PortalWaitingHeading css={{ color: fontColor }}>
                    Submitting request...
                  </PortalWaitingHeading>
                </PortalWaitingContent>
              </PortalWaiting>
            )}
            {captivePortalRequest.isSuccess && (
              <PortalWaiting>
                <PortalWaitingContent>
                  <PortalWaitingHeading css={{ color: fontColor }}>
                    Your request has been submitted for approval.
                  </PortalWaitingHeading>
                  <PortalWaitingSubheading css={{ color: fontColor }}>
                    You may close this window.
                  </PortalWaitingSubheading>
                </PortalWaitingContent>
              </PortalWaiting>
            )}
            {captivePortalRequest.isError && (
              <PortalWaiting>
                <PortalWaitingContent>
                  <PortalWaitingHeading css={{ color: fontColor }}>
                    There was a problem processing your request.
                  </PortalWaitingHeading>
                </PortalWaitingContent>
              </PortalWaiting>
            )}
          </>
        )}
        <PortalFooter>
          {captivePortalRequest.isIdle && (
            <>
              <PortalButton
                recolor={lightOrDarkBackground}
                onClick={onClickConfirm}
                href={dynamicConfirmLink ?? '#'}
              >
                <PortalButtonLabel {...buttonLabelProps}>
                  {captivePortal.callToAction ?? 'Connect'}
                </PortalButtonLabel>
              </PortalButton>
              {captivePortal.termsAndConditions && (
                <PortalTerms css={{ color: fontColor }}>
                  By connecting, you agree to our{' '}
                  <PortalTermsLink
                    tabIndex={0}
                    onClick={() => termsDialogState.open()}
                    recolor={lightOrDarkBackground}
                    css={{ color: callToActionColor }}
                  >
                    terms & conditions
                  </PortalTermsLink>
                </PortalTerms>
              )}
            </>
          )}
          {!captivePortal?.displaySettings?.hidePoweredByMeter && (
            <PortalBranded
              recolor={lightOrDarkBackground}
              rel="noopener noreferrer"
              href="https://meter.com"
              target="_blank"
            >
              <PortalBrandedLabel {...poweredByProps}>Powered by</PortalBrandedLabel>
              <PortalBrandedLogo recolor={lightOrDarkBackground} size={18} type="word" />
            </PortalBranded>
          )}
        </PortalFooter>
      </PortalContent>
      {captivePortal.termsAndConditions && (
        <PortalTermsDialog
          state={termsDialogState}
          termsAndConditions={captivePortal.termsAndConditions}
        />
      )}
    </PortalWrapper>
  );
}
